import { ApolloError } from '@apollo/client'
import { UseToastOptions } from '@chakra-ui/react'
import { Maybe, Property } from 'generated/graphql'
import get from 'lodash/get'
import { ReactNode } from 'react'
import { ERROR_TOAST, STRAPI_USER_STORAGE_KEY, WARNING_TOAST } from '../constants'
import { images } from 'theme'
import { TFunction } from 'react-i18next'
/**
 * Gets JWT of authenticatedUser from either sessionStorage or localStorage
 */
export const fetchJwt = (): string | null => {
  const localUser = localStorage.getItem(STRAPI_USER_STORAGE_KEY)
  const sessionUser = sessionStorage.getItem(STRAPI_USER_STORAGE_KEY)
  const user = sessionUser || localUser

  return user ? JSON.parse(user).jwt : null
}

export function formatError({ response }: { response: any }): string {
  return get(response, 'data.message[0].messages[0].message', 'An unknown error has occured.')
}

/**
 * function to return a handle errors returned from GraphQL queries/mutations.
 * @param message error message returned and destructured
 * @param toast useToast from Chakra
 * @param description optional *user friendly* message. Recommended
 * not using the graphQL error message.
 * recommended usage:
 * onError: (e) => handleErrors(e, toast, 'fetching your data')
 */

export const handleErrors = (
  { message }: ApolloError | Error,
  toast: (props: UseToastOptions) => ReactNode | void,
  description?: string
): ReactNode | void => {
  if (message.includes('403')) {
    return toast({ description: message, ...WARNING_TOAST })
  }
  return toast({
    ...ERROR_TOAST,
    title: description
      ? `Something went wrong with ${description}.`
      : message.replace('GraphQL error:', '').trim()
  })
}

export const generatePrices = (
  length = 15,
  minPrice = 50,
  dif = 150
): {
  arr: number[]
  min: number
  max: number
} => {
  const arr: number[] = []

  while (arr.length <= length) {
    arr.push(minPrice)
    minPrice += dif
  }

  return { arr, min: arr[0], max: arr[arr.length - 1] }
}

export type FormatPriceOptions = { locale?: string; currency?: string }
// Create our number formatter.
export const amountFormatter = (
  value: number,
  opts: { locale?: string; currency?: string } = {}
): string => {
  const { locale = 'en-US', currency = 'USD' } = opts
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  })

  return formatter.format(value).replace(',', ' ')
}

type GetUniqValuesArrType<T> = T & { _key?: Maybe<string> | undefined }
export const getUniqValuesArr = <T>(
  arr1: GetUniqValuesArrType<T>[],
  arr2: GetUniqValuesArrType<T>[]
): T[] => arr1.filter((u) => arr2.findIndex((lu) => lu?._key === u?._key) === -1)

export function getImageByFileExtension(fileName: string): any {
  const fileExtension = fileName.split('.').pop()
  switch (fileExtension) {
    case 'pdf':
      return images.pdf
    case 'doc':
    case 'docx':
      return images.word
    case 'xls':
    case 'xlsx':
      return images.excel
    case 'ppt':
    case 'pptx':
      return images.powerpoint
    case 'zip':
    case 'rar':
      return 'zip.svg'
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'image.svg'
    case 'mp3':
    case 'wav':
    case 'aac':
      return 'audio.svg'
    case 'mp4':
    case 'avi':
    case 'mkv':
      return 'video.svg'
    default:
      return images.logo_purple || ''
  }
}

export const getColorPerStatus = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'orange'
    case 'approved':
      return 'green'
    case 'rejected':
      return 'red'
    case 'active':
      return 'green'
    case 'archived':
      return 'red'
    case 'inactive':
      return 'red'
    case 'completed':
      return 'green'
    case 'admin':
      return 'orange'
    case 'agent':
      return 'purple'
    default:
      return 'gray'
  }
}

export const formatPropertyTitle = (
  property: Property,
  t: TFunction<'translation'>,
  lang: string
): string => {
  return `${property?.bedrooms} ${
    property?.bedrooms || 0 > 1 ? t('search_results.bedroom') : t('search_results.bedroom') + 's'
  } ${(property?.type_of_property as any)?.[`name_${lang}`]} ${
    property?.property_category?.name
  } ${t('search_results.at')} ${property?.suburb?.name}`
}
